import { LOADING } from 'components/users/flexlist/FlexList';
import { EMPTY_ARRAY } from 'constants/defaults';
import { uniq } from 'lodash-es';
import { DateTime } from 'luxon';
import { getUserCapacitiesEntities } from 'redux/modules/capacities';
import { getSummaryEntities } from 'redux/modules/summaries';
import {
  getDemoInvitedUserCount,
  getDemoIsFetchingUsers,
  getDemoListLoadedSummaryCount,
  getDemoListRows,
  getDemoListTotals,
  getDemoListUserIdsToLoad,
  getDemoSummedUserCapacity,
  getDemoUserCount,
} from 'redux/modules/users-page/demo/usersPage.demo.selectors';
import type { ListUser, Query } from 'redux/modules/users-page/users-list/usersList.types';
import {
  getAccountFeature,
  getAccountWeeklyCapacity,
  getCurrentAccount,
} from 'redux/selectors/accounts';
import { getHoursAsCards } from 'redux/selectors/hours';
import { getTeamEntities, getTeamResult, NO_TEAM_ASSIGNMENT } from 'redux/selectors/teams';
import { getUserPermissions } from 'redux/selectors/user';
import { getActiveUsers, getDeletedUsers, getInvitedUsers } from 'redux/selectors/users';
import { calculateTotalCapacityForPeriod } from 'redux/selectors/utils/capacities';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import { SUMMARY_API_LIMIT } from 'types/summaries';
import type { User } from 'types/user';
import type { PickByValue } from 'types/utility';
import { summarizeCostAndDuration } from 'utils/hours';
import type { SortableValue } from 'utils/naturalSort';
import { naturalCompare } from 'utils/naturalSort';
import {
  createDefaultUserCapacities,
  durationSummary,
  formatDayRange,
  userSummary,
} from 'utils/summary';
import { keyBy, notNull } from 'utils/typesafe';

const readUsersListTotals = (state: IState) => state.usersPage.list.totals;
const readUsersListQuery = (state: IState) => state.usersPage.list.query;
const readXhrStatusUsers = (state: IState) => state.users.xhrStatus;
const readUsersListControl = (state: IState) => state.usersPage.list.control;
const readFilter = (state: IState) => state.usersPage.list.filters;

const cacheKeyFromQuery = (query: Query) => `${query.from}-${query.to}`;

export const getUsersListQuery = readUsersListQuery;
export const getFilter = readFilter;

export const getRealUsersListTotals = createSelector(
  [readUsersListTotals, getUsersListQuery],
  (totals, query) => {
    return durationSummary(totals[cacheKeyFromQuery(query)]);
  }
);

export const getUsersListTotals = createSelector(
  [getDemoListTotals, getRealUsersListTotals],
  (demo, real) => demo ?? real
);

export const getControlDayRange = createSelector([getUsersListQuery], query =>
  formatDayRange(query.from, query.to)
);

export const getRealFetchingUsers = createSelector([readXhrStatusUsers], xhrStatus => {
  const hasFetchedUsers = xhrStatus.fetched.some(({ key }) => key.includes('users'));
  const isFetchingUsers = xhrStatus.fetching.some(({ key }) => key.includes('users'));
  if (!hasFetchedUsers) {
    return true;
  }
  return isFetchingUsers;
});

export const getFetchingUsers = createSelector(
  [getDemoIsFetchingUsers, getRealFetchingUsers],
  (demo, real) => demo ?? real
);

export const getUsers = createSelector(
  [getUsersListQuery, getActiveUsers, getInvitedUsers, getDeletedUsers],
  (query, activeUsers, invitedUsers, deletedUsers) => {
    switch (query?.show) {
      case 'deleted':
        return deletedUsers;
      case 'invited':
        return invitedUsers;
      default:
        return activeUsers;
    }
  }
);

export const getUserEntities = createSelector([getUsers], users => keyBy(users, 'id'));

export const getSortedUsers = createSelector([getUsersListQuery, getUsers], (query, users) => {
  const sortBy = query.sort.by as keyof PickByValue<User, SortableValue>;
  const sortDirection = query.sort.direction;

  return users.sort((a, b) =>
    sortDirection === 'asc'
      ? naturalCompare(b[sortBy], a[sortBy])
      : naturalCompare(a[sortBy], b[sortBy])
  );
});

export const getUsersForFilter = getUsers;
export const getFilterableUserIdsForList = createSelector([getUsersForFilter], users =>
  users.map(u => u.id)
);

export const getIdsForUsersWithTeams = createSelector(
  [getTeamResult, getTeamEntities],
  (teamIds, teams) => {
    return teamIds
      .map(t => teams[t].users.map(({ user_id }) => user_id))
      .reduce((memo, ids) => {
        const uniques = ids.filter(id => !memo.includes(id));
        memo.push(...uniques);
        return memo;
      }, []);
  }
);

export const getIdsForUsersWithoutTeam = createSelector(
  [getUsers, getIdsForUsersWithTeams],
  (users, idsForUsersWithTeams) =>
    users.filter(user => !idsForUsersWithTeams.includes(user.id)).map(user => user.id)
);

const getUserIdsFromSelectedTeams = createSelector(
  [getFilter, getTeamEntities, getIdsForUsersWithoutTeam, getSortedUsers],
  (filter, teams, usersWithoutTeams, users) => {
    const { teamIds } = filter;

    if (!Array.isArray(teamIds)) {
      return users.map(user => user.id);
    }

    const additionalUsers = teamIds.includes(NO_TEAM_ASSIGNMENT) ? usersWithoutTeams : [];
    const sanitizedTeamIds = teamIds.filter(id => id !== NO_TEAM_ASSIGNMENT);

    return uniq(
      sanitizedTeamIds
        .flatMap(teamId => teams[teamId].users.map(user => user.user_id))
        .filter(notNull)
        .concat(additionalUsers)
    );
  }
);

export const getFilteredUsers = createSelector(
  [getSortedUsers, getFilter],
  (sortedUsers, filter) => {
    const { userIds } = filter;
    if (!Array.isArray(userIds)) {
      return sortedUsers;
    }
    return sortedUsers.filter(u => userIds.includes(u.id));
  }
);

export const getFilteredUserIds = createSelector([getFilteredUsers], users => users.map(u => u.id));

export const getFilteredTeamIds = createSelector([getFilter, getTeamEntities], (filter, teams) => {
  const { teamIds } = filter;
  if (!Array.isArray(teamIds)) {
    return Object.keys(teams).map(id => Number(id));
  }
  return teamIds;
});

// selected users - either by userIds or teamIds
export const getUsersSelectedByUserIdsOrTeamIds = createSelector(
  [getFilter, getUserIdsFromSelectedTeams, getFilteredUserIds, getSortedUsers],
  (filter, userIdsFromSelectedTeams, filteredUserIds, sortedUsers) => {
    // Check if an actual filter is applied (teamIds or userIds is an array)
    const hasTeamFilter = Array.isArray(filter.teamIds);
    const hasUserFilter = Array.isArray(filter.userIds);

    let idsToSelect: number[];
    if (hasTeamFilter) {
      // Team filter takes priority
      idsToSelect = userIdsFromSelectedTeams;
    } else if (hasUserFilter) {
      // User filter
      idsToSelect = filteredUserIds;
    } else {
      // No filter - return all users
      return sortedUsers;
    }

    return sortedUsers.filter(u => idsToSelect.includes(u.id));
  }
);

export const getUserIdsSelectedByUserIdsOrTeamIds = createSelector(
  [getUsersSelectedByUserIdsOrTeamIds],
  users => users.map(u => u.id)
);

// this is used for the users/list route
export const getRealUsersWithSummary = createSelector(
  [
    (state: IState) => getAccountFeature(state, 'weekly_user_capacity'),
    getUsersListQuery,
    getUsersSelectedByUserIdsOrTeamIds,
    getSummaryEntities,
    getUserCapacitiesEntities,
  ],
  (hasWeeklyUserCapacityFeature, query, users, summaries, userCapacityEntities) => {
    const key = cacheKeyFromQuery(query);
    return users.map(({ id, avatar, ...rest }) => {
      const summaryById = summaries[id] || null;
      const summary = summaryById?.byKey?.[key] ? summaryById.byKey[key] : null;
      const url = `/users/${id}`;
      // User is loaded if we have their summary data (or feature disabled, or user deleted)
      // Summaries are loaded on scroll via loadMoreRows
      return {
        id,
        url,
        avatar: avatar ? avatar.large_retina : null,
        summary: userSummary(summary, query.from, query.to, userCapacityEntities?.[id] || null),
        isLoaded: !!(summary || !hasWeeklyUserCapacityFeature || rest.deleted),
        status: summaryById ? summaryById.status : LOADING,
        ...rest,
      } as ListUser;
    });
  }
);

export const getUsersWithSummary = createSelector(
  [getDemoListRows, getRealUsersWithSummary],
  (demo, real) => demo ?? real
);

export const getLoadedUsersWithSummary = createSelector([getUsersWithSummary], users =>
  users.filter(u => u.isLoaded)
);

/**
 * Whether the list needs `/users/summary` data at all. The capacity/logged
 * columns need it (weekly_user_capacity), and so does the daily Control grid
 * (features.control) — those are independent account flags. Summary loading must
 * not be driven by the presentational `isLoaded` flag, which is unconditionally
 * true when weekly_user_capacity is off and would otherwise stop all fetching on
 * Control-only accounts, leaving the grid permanently empty. (TT-5478)
 */
const getListNeedsSummaries = createSelector(
  [
    (state: IState) => getAccountFeature(state, 'weekly_user_capacity'),
    (state: IState) => getAccountFeature(state, 'control'),
  ],
  (hasWeeklyUserCapacityFeature, hasControlFeature) =>
    !!(hasWeeklyUserCapacityFeature || hasControlFeature)
);

// Summary presence per selected user, keyed off the actual summary store rather
// than the presentational `isLoaded` flag. Deleted users never get summaries.
const getSelectedUsersSummaryPresence = createSelector(
  [getUsersSelectedByUserIdsOrTeamIds, getSummaryEntities, getUsersListQuery],
  (users, summaries, query) => {
    const key = cacheKeyFromQuery(query);
    return users.map(user => ({
      id: user.id,
      deleted: !!user.deleted,
      hasSummary: !!summaries[user.id]?.byKey?.[key],
    }));
  }
);

export const getRealUserIdsToLoad = createSelector(
  [getListNeedsSummaries, getSelectedUsersSummaryPresence],
  (needsSummaries, presence) => {
    if (!needsSummaries) {
      return EMPTY_ARRAY as number[];
    }
    return presence
      .filter(user => !user.deleted && !user.hasSummary)
      .map(user => user.id)
      .slice(0, SUMMARY_API_LIMIT);
  }
);

export const getUserIdsToLoad = createSelector(
  [getDemoListUserIdsToLoad, getRealUserIdsToLoad],
  (demo, real) => demo ?? real
);

// Count of users (from the top of the sorted list) whose summary has loaded — the
// frontier the list loads the next batch at. When no summaries are needed, treat
// every row as loaded so the list never tries to fetch.
export const getRealLoadedSummaryCount = createSelector(
  [getListNeedsSummaries, getSelectedUsersSummaryPresence],
  (needsSummaries, presence) => {
    if (!needsSummaries) {
      return presence.length;
    }
    return presence.filter(user => user.deleted || user.hasSummary).length;
  }
);

export const getLoadedSummaryCount = createSelector(
  [getDemoListLoadedSummaryCount, getRealLoadedSummaryCount],
  (demo, real) => demo ?? real
);

export const getLoadedUsersCacheSignature = createSelector([getUsersWithSummary], users => {
  return users.filter(u => u.isLoaded).map(u => ({ id: u.id, updated_at: u.updated_at }));
});

export const getControlEnabled = createSelector(
  [getCurrentAccount],
  account => account.features.control
);

export const getRealSummedUserCapacity = createSelector(
  [
    getUsersListQuery,
    getUserCapacitiesEntities,
    getAccountWeeklyCapacity,
    getUsersSelectedByUserIdsOrTeamIds,
  ],
  (query, userCapacityEntities, accountWeeklyCapacity, selectedUsers) => {
    if (!query.from || !query.to) {
      return 0;
    }

    const momentFrom = DateTime.fromISO(query.from);
    const momentTo = DateTime.fromISO(query.to);

    const numDaysInRange = momentTo.diff(momentFrom, 'days').days;
    if (numDaysInRange < 0) {
      return 0;
    }

    const defaultUserCapacities = createDefaultUserCapacities(accountWeeklyCapacity);
    return selectedUsers
      .filter(user => user.active)
      .reduce((sum, user) => {
        const userCapacity = calculateTotalCapacityForPeriod(
          query.from,
          query.to,
          userCapacityEntities?.[user.id] ?? defaultUserCapacities
        );

        return sum + userCapacity;
      }, 0);
  }
);

export const getSummedUserCapacity = createSelector(
  [getDemoSummedUserCapacity, getRealSummedUserCapacity],
  (demo, real) => demo ?? real
);

export const getShowUserActions = createSelector(
  [getUserPermissions],
  permissions =>
    permissions.canUpdate.users || permissions.canDelete.users || permissions.canNotify.users
);

export const getDetailsRef = createSelector([readUsersListControl], control => control.detailsRef);

export const getHoursForDetails = createSelector(
  [getDetailsRef, getHoursAsCards],
  (details, hourCards) => {
    if (!details) return EMPTY_ARRAY;
    return hourCards.filter(hour => hour.user_id === details.userId && hour.day === details.date);
  }
);

export const getUserForDetails = createSelector(
  [getDetailsRef, getUserEntities],
  (details, userEntities) => {
    return details ? userEntities[details.userId] : null;
  }
);

export const summarizeHoursForDetails = createSelector([getHoursAsCards], hours => {
  return summarizeCostAndDuration(hours);
});

/**
 * Counts for the Active / Invited / Deleted tabs above the All Users list. Reading one
 * selector keeps the three labels on the same source of truth; the demo directory has
 * no invited or deleted people, so those tabs read zero.
 */
export const getRealUsersListTabCounts = createSelector(
  [getActiveUsers, getInvitedUsers, getDeletedUsers],
  (activeUsers, invitedUsers, deletedUsers) => ({
    active: activeUsers.length,
    invited: invitedUsers.length,
    deleted: deletedUsers.length,
  })
);

const getDemoUsersListTabCounts = createSelector(
  [getDemoUserCount, getDemoInvitedUserCount],
  (activeCount, invitedCount) =>
    activeCount === undefined
      ? undefined
      : { active: activeCount, invited: invitedCount ?? 0, deleted: 0 }
);

export const getUsersListTabCounts = createSelector(
  [getDemoUsersListTabCounts, getRealUsersListTabCounts],
  (demo, real) => demo ?? real
);
